/**
 * TOP FAILING CHART
 *
 * Horizontal bar chart listing scenarios by fail count. Recharts renders each
 * scenario on its own row with the fail count as the bar length.
 *
 * Props:
 *   - scenarios: Array<{ scenarioKey, scenarioName, failCount }>
 *   - height: number (default auto-scales with row count)
 */

import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts'
import { token } from '@atlaskit/tokens'

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null
  const { scenarioKey, scenarioName, failCount, totalExecutions, failRate } = payload[0].payload
  return (
    <div
      style={{
        backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
        border: `1px solid ${token('color.border', '#DFE1E6')}`,
        borderRadius: '3px',
        padding: '8px 10px',
        fontSize: '12px',
        maxWidth: '280px',
        boxShadow: '0 2px 6px rgba(9, 30, 66, 0.25)'
      }}
    >
      <div style={{ fontWeight: 600, color: token('color.text', '#172B4D') }}>
        {scenarioKey ? `${scenarioKey} · ` : ''}{scenarioName}
      </div>
      <div style={{ marginTop: '4px', color: token('color.text.subtle', '#6B778C') }}>
        Fails: <strong style={{ color: token('color.text.danger', '#DE350B') }}>{failCount}</strong>
        {' '}of {totalExecutions} ({Number(failRate || 0).toFixed(1)}%)
      </div>
    </div>
  )
}

const TopFailingChart = ({ scenarios = [] }) => {
  // Recharts horizontal bars need `layout="vertical"` (swaps axes). Each row
  // needs a unique string label for the Y axis — use the scenario key when
  // present, fall back to id.
  const data = useMemo(
    () =>
      (scenarios || []).map((s) => ({
        ...s,
        label: s.scenarioKey || `Scenario ${s.scenarioId}`
      })),
    [scenarios]
  )

  // Auto-scale height so each row gets ~40px.
  const chartHeight = Math.max(160, data.length * 40 + 40)

  if (!data.length) {
    return (
      <div
        style={{
          padding: '32px',
          textAlign: 'center',
          color: token('color.text.subtle', '#6B778C'),
          fontSize: '13px',
          border: `1px dashed ${token('color.border', '#DFE1E6')}`,
          borderRadius: '4px'
        }}
      >
        No failed executions in this release — nice work.
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 24, left: 80, bottom: 8 }}
      >
        <CartesianGrid stroke={token('color.border', '#DFE1E6')} strokeDasharray="3 3" />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
          width={80}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: token('color.background.neutral', '#F4F5F7') }} />
        <Bar
          dataKey="failCount"
          fill={token('color.background.danger.bold', '#DE350B')}
          radius={[0, 2, 2, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

export default TopFailingChart
